// src/pages/PostView.js
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { 
  Container, 
  Box, 
  Paper, 
  CircularProgress, 
  Typography, 
  Button,
  Divider,
  Breadcrumbs 
} from '@mui/material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { collection, doc, getDoc, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import Post from '../features/collaboration/components/Post';
import { likePost, commentOnPost, trackPostShare } from '../features/collaboration/services/collaborationService';
import { ArrowBack as ArrowBackIcon, Home as HomeIcon } from '@mui/icons-material';
import { THEME_COLORS } from '../styles/theme';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/images/logo.png';

const PostView = () => {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        
        // Get the post document
        const postDoc = await getDoc(doc(db, "posts", postId));
        
        if (!postDoc.exists()) {
          setError("Post not found");
          return;
        }
        
        const postData = { id: postDoc.id, ...postDoc.data() };
        
        // Get author info (only if authenticated; userProfiles require auth)
        if (currentUser) {
          const authorDoc = await getDoc(doc(db, "userProfiles", postData.authorId));
          postData.author = authorDoc.exists() 
            ? { id: authorDoc.id, ...authorDoc.data() }
            : { id: postData.authorId, name: "Unknown User" };
        } else {
          postData.author = { id: postData.authorId, name: "Educator" };
        }
        
        // Get comments
        const commentsRef = collection(db, "posts", postId, "comments");
        const commentsQuery = query(commentsRef, orderBy("createdAt", "asc"));
        const commentsSnapshot = await getDocs(commentsQuery);
        
        postData.comments = [];
        for (const commentDoc of commentsSnapshot.docs) {
          const comment = { id: commentDoc.id, ...commentDoc.data() };
          
          // Get user info for each comment (only if authenticated)
          if (currentUser) {
            const userDoc = await getDoc(doc(db, "userProfiles", comment.userId));
            comment.user = userDoc.exists() 
              ? { id: userDoc.id, ...userDoc.data() }
              : { id: comment.userId, name: "Unknown User" };
          } else {
            comment.user = { id: comment.userId, name: "User" };
          }
          
          postData.comments.push(comment);
        }
        
        // Check if current user has liked this post
        if (currentUser && postData.likedBy) {
          postData.likedByCurrentUser = postData.likedBy.includes(currentUser.uid);
        }
        
        setPost(postData);
      } catch (err) {
        console.error("Error fetching post:", err);
        setError("Failed to load post. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    
    if (postId) {
      fetchPost();
    }
  }, [postId, currentUser]);
  
  const handleLike = async (postId, userId) => {
    try {
      const result = await likePost(postId, userId);
      
      // Update the local post state
      setPost(prev => ({
        ...prev,
        likes: result.liked ? (prev.likes || 0) + 1 : Math.max(0, (prev.likes || 0) - 1),
        likedByCurrentUser: result.liked
      }));
      
      return result;
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };
  
  const handleComment = async (postId, commentData) => {
    try {
      return await commentOnPost(postId, commentData.userId, commentData.text);
    } catch (error) {
      console.error('Error posting comment:', error);
    }
  };
  
  const handleShare = async (postId, platform) => {
    if (currentUser?.uid) {
      try {
        await trackPostShare(postId, currentUser.uid, platform);
      } catch (error) {
        console.error('Error sharing post:', error);
      }
    }
  };

  const getMetaTags = () => {
    if (!post) return null;

    const longFormTitle = post?.article?.title || null;
    const postLabel = post?.postType === 'research' ? 'deep research' : post?.postType === 'article' ? 'article' : 'post';
    const postTitle = longFormTitle || `${post.author?.name || 'Educator'}'s ${postLabel} on EduPlatform`;
    let postDescription = post.content || '';
    if (postDescription.length > 160) {
      postDescription = postDescription.substring(0, 157) + '...';
    }
    
    // Determine image URL
    let imageUrl = null;
    if (post.image) {
      imageUrl = post.image;
    } else if (post.mediaType === 'image' && post.mediaUrl) {
      imageUrl = post.mediaUrl;
    }
    
    return (
      <Helmet>
        <title>{postTitle}</title>
        <meta name="description" content={postDescription} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={postTitle} />
        <meta property="og:description" content={postDescription} />
        <meta property="og:url" content={window.location.href} />
        {imageUrl && <meta property="og:image" content={imageUrl} />}
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={postTitle} />
        <meta name="twitter:description" content={postDescription} />
        {imageUrl && <meta name="twitter:image" content={imageUrl} />}
      </Helmet>
    );
  };
  
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>

    {!currentUser && (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 3
        }}
      >
        <img
          src={logo}
          alt="Teachmate Logo"
          style={{ height: 48, width: 'auto' }}
        />
      </Box>
    )}

    {post && getMetaTags()}

      <Paper elevation={3} sx={{ p: 0, borderRadius: 2, overflow: 'hidden' }}>
        <Box mb={3}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link 
              to="/"
              style={{ 
                display: 'flex', 
                alignItems: 'center',
                color: THEME_COLORS.primary,
                textDecoration: 'none'
              }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
              Home
            </Link>
            <Link 
              to="/collaboration"
              style={{ 
                color: THEME_COLORS.primary,
                textDecoration: 'none'
              }}
            >
              Educator Feed
            </Link>
            <Typography color="text.primary">Post</Typography>
          </Breadcrumbs>
        </Box>
        
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        
        <Divider sx={{ mb: 3 }} />
        
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress sx={{ color: THEME_COLORS.primary }} />
          </Box>
        ) : error ? (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="error" gutterBottom>
              {error}
            </Typography>
            <Button 
              variant="contained"
              onClick={() => navigate('/collaboration')}
              sx={{ 
                mt: 2,
                backgroundColor: THEME_COLORS.primary,
                '&:hover': { backgroundColor: '#1e1c4e' }
              }}
            >
              Return to Feed
            </Button>
          </Box>
        ) : (
          <Post
            post={post}
            onLike={handleLike}
            onComment={handleComment}
            onShare={handleShare}
            onDelete={() => navigate('/collaboration')}
            fullView
          />
        )}
      </Paper>
    </Container>
  );
};

export default PostView;
